import appConfig from '../../config/appConfig';
import {getDeviceTypesData} from '../../consts/device/deviceTypesData';
import * as iccProductsStatusMessageTypes from '../../consts/icc/iccProductsStatusMessageTypes';
import DEVICE_TYPES from '../../enums/device/deviceTypes';
import ICC_PRODUCT_STATUS_TYPES from '../../enums/icc/iccProductStatusTypes';
import {selectIotDeviceData} from '../../state/selectors/iotDevice';
import {getState} from '../../state/store';
import dateTimeUtils from '../../utils/dateTimeUtils';
import stringUtils from '../../utils/stringUtils';
import codentifyTransformService from '../codentifyTransformService';
import devicePlatformService from '../device/devicePlatformService';
import deviceTypesService from '../device/deviceTypesService';
import marketConfigService from '../marketConfigService';
import productHelpers from '../product/productHelpers';

export const aggregatedSettingsMapping = (data) => ({
    allMandatoryTermsApproved: data.AllMandatoryTermsApproved,
    allNonMandatoryTermsReviewed: data.AllNonMandatoryTermsReviewed,
    settingsRefreshTimeoutSec: data.SettingsRefreshTimeoutSec,
    consumerAggregatedSettings: data.ConsumerAggregatedSettings.map(consumerAggregatedSettingsMapping),
    intToken: data.IntToken,
});

const consumerAggregatedSettingsMapping = (data) => ({key: data.Key, value: data.Value?.toLowerCase() === 'true'});

export const consumerMapping = (data) => ({
    consumerId: data.ConsumerID,
    defaultLocaleCode: data.DefaultLocaleCode,
});

export const externalConsumerMapping = (data) => ({
    consumerId: data.ConsumerID,
    defaultLocaleCode: data.DefaultLocaleCode,
    hav: data.Hav,
    firstName: data.Firstname,
});

export const consumerProductsMapping = (products) => {
    const mappedProducts = products.map((product) => consumerProductMapping(product));
    const notEmptyProducts = mappedProducts.filter((x) => x);

    return notEmptyProducts;
};

const getDeviceSerialNumberByCodentify = (codentify) => {
    const {device, holder} = selectIotDeviceData(getState()) || {};
    const {codentifyOriginal: iotDeviceCodentify, deviceSerialNumber: iotDeviceSerialNumber} = device || {};
    const transformedDeviceCodentify = codentifyTransformService.transformCodentifyForIcc(iotDeviceCodentify);

    if (codentify === iotDeviceCodentify || codentify === transformedDeviceCodentify) {
        return iotDeviceSerialNumber;
    }

    const {codentifyOriginal: iotHolderCodentify, deviceSerialNumber: iotHolderSerialNumber} = holder || {};
    const transformedHolderCodentify = codentifyTransformService.transformCodentifyForIcc(iotHolderCodentify);

    if (codentify === iotHolderCodentify || codentify === transformedHolderCodentify) {
        return iotHolderSerialNumber;
    }
};

const consumerProductMapping = (data) => {
    const codentify = data.CodentifyID;
    const deviceSerialNumber = data.DeviceSerialNumber || getDeviceSerialNumberByCodentify(codentify);

    if (!deviceSerialNumber) return null;

    const deviceType = devicePlatformService.getDeviceTypeBySerialNumber(deviceSerialNumber);
    const deviceTypeData = getDeviceTypesData()[deviceType];

    if (!deviceTypeData) return null;

    const isDeviceAlreadyYoursStatusMessage = data.StatusMessage === iccProductsStatusMessageTypes.DEVICE_ALREADY_YOURS; //this is only needed to ignore the status message DEVICE_ALREADY_YOURS and not change status to failed
    const transformedDeviceSerialNumber = productHelpers.getTransformedDeviceSerialNumber(deviceSerialNumber);

    return {
        deviceSerialNumber: transformedDeviceSerialNumber,
        type: deviceType,
        codentify,
        productId: data.ProductID,
        pairingStatus: data.PairingStatus,
        productName: data.ProductName,
        status: isDeviceAlreadyYoursStatusMessage ? ICC_PRODUCT_STATUS_TYPES.REGISTERED : data.Status,
        isHolder: deviceTypeData.isHolder,
        purchaseDate: new Date(data.PurchasedOn).toUTCString(),
        statusMessage: data.StatusMessage,
    };
};

export const consumerProductsStatusMapping = (data) => data.map(deviceRegistrationMapping);

export const consumerProductsInfoMapping = (products) => {
    const mappedProducts = products.map(consumerProductInfoMapping);
    const notEmptyProducts = mappedProducts.filter((x) => x);

    return notEmptyProducts;
};

const consumerProductInfoMapping = (product) => {
    const deviceSerialNumber = product?.DeviceSerialNumber;

    if (!deviceSerialNumber) return null;

    const material = getMaterialMapping(product.Material, product.Product);
    const type = devicePlatformService.getDeviceTypeBySerialNumber(deviceSerialNumber);
    const isOriginalDuoDevice = deviceTypesService.isOriginalsDuoDeviceByCodeOrMaterial({
        material: {materialBrandFamily: material?.materialBrandFamily},
    });
    const preparedType =
        DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER === type && isOriginalDuoDevice
            ? DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER
            : type;
    const isDeviceSupported = marketConfigService.isDeviceSupported(preparedType);
    const transformedDeviceSerialNumber = productHelpers.getTransformedDeviceSerialNumber(deviceSerialNumber);

    return {
        deviceSerialNumber: transformedDeviceSerialNumber,
        mediaId: getMediaId(material, preparedType),
        material,
        //TODO: remove brandFamily and use material.materialBrandFamily
        brandFamily: material?.materialBrandFamily,
        pairingStatus: product.PairingStatus,
        productName: product.ProductName,
        defaultProductName: deviceTypesService.getProductNameByType(preparedType),
        isDeviceSupported,
        type: preparedType,
        codentifyID: product.CodentifyID,
        //TODO: remove materialColor and use material.materialColor
        materialColor: material?.materialColor,
    };
};

const getMaterialMapping = (material, product) => {
    if (!material) return null;

    return {
        materialGroup: material.MaterialGroup,
        materialBrandFamily: material.MaterialBrandFamily,
        materialModelNumber: material.MaterialModelNumber,
        materialColor: material.MaterialColor,
        materialBrandDifferentiator: product?.ProductDifferentiator,
    };
};

const getMediaId = (material, deviceType) => {
    try {
        const materialColor = material.materialColor;
        const isUseBrandDiff = productHelpers.isUseProductBrandDifferentiator(deviceType, materialColor);
        const color = isUseBrandDiff ? material.materialBrandDifferentiator || materialColor : materialColor;
        const mediaIdParts = [
            material.materialGroup,
            material.materialBrandFamily,
            material.materialModelNumber,
            color,
        ];
        const mediaName = mediaIdParts.join('_');

        return assetMapping(mediaName);
    } catch (e) {
        return null;
    }
};

export const holderAssetMapping = (mediaName) => mediaIdMapping(mediaName, appConfig.getHolderAssetsPath());
export const assetMapping = (mediaName) => mediaIdMapping(mediaName, appConfig.getAssetsPath());
export const mediaIdMapping = (mediaName, assetsPath) => stringUtils.formatString(assetsPath, mediaName);

export const mediaUrlsMapping = (data) =>
    data.map((item) => {
        const expirationDate = new Date(item.ExpirationDate).getTime();

        return {
            mediaId: item.MediaId,
            url: item.Url,
            expirationDate,
            isExpired: () => dateTimeUtils.isExpired(expirationDate),
        };
    });

export const deviceRegistrationMapping = (data) => {
    const isDeviceAlreadyYoursStatusMessage = data.StatusMessage === iccProductsStatusMessageTypes.DEVICE_ALREADY_YOURS; //this is only needed to ignore the status message DEVICE_ALREADY_YOURS and not change status to failed
    const transformedDeviceSerialNumber = productHelpers.getTransformedDeviceSerialNumber(data.DeviceSerialNumber);

    return {
        codentify: data.CodentifyID,
        deviceSerialNumber: transformedDeviceSerialNumber,
        productID: data.ProductID,
        parentProductId: data.ParentProductID,
        status: isDeviceAlreadyYoursStatusMessage ? ICC_PRODUCT_STATUS_TYPES.REGISTERED : data.Status,
        statusMessage: isDeviceAlreadyYoursStatusMessage ? null : data.StatusMessage,
    };
};

export const deviceUnregistrationMapping = (data) => {
    const unregistrationData = data[0];

    return {
        codentify: unregistrationData.CodentifyID,
        deviceSerialNumber: unregistrationData.DeviceSerialNumber,
        productID: unregistrationData.ProductID,
        parentProductId: unregistrationData.ParentProductID,
        status: unregistrationData.Status,
        statusMessage: unregistrationData.StatusMessage,
    };
};

export const tncMapping = (data) => data.map(tncItemMapper);

const tncItemMapper = (data) => ({
    description: data.Description,
    updateDescription: data.UpdateDescription,
    titleDescription: data.TitleDescription,
    groupName: data.GroupName,
    isMandatory: data.IsMandatory,
    legals: data.Legals.map(tncLegalMapper),
    position: data.Position,
    screenId: data.ScreenId,
});

const tncLegalMapper = (data) => ({
    changeList: data.ChangeList,
    code: data.Code,
    description: data.Description,
    externalTermAndConditionID: data.ExternalTermAndConditionID,
    isInformational: data.IsInformational,
    isMandatory: data.IsMandatory,
    isRevoked: data.IsRevoked,
    position: data.Position,
    settings: data.Settings,
    text: data.Text,
    title: data.Title,
    versionNumber: data.VersionNumber,
    webLink: data.WebLink,
    alternativeText: data.AlternativeText,
    updateTs: data.UpdateTs,
});
